/**
 * Load More Pagination
 * Uses Shopify's Section Rendering API to fetch only the collection section
 * instead of the full page, reducing memory usage on mobile.
 */

class LoadMorePagination {
  constructor() {
    this.container = document.querySelector('[data-products-grid]');
    this.paginationWrapper = document.querySelector('[data-pagination]');

    if (!this.container || !this.paginationWrapper) return;

    // Get the section ID from the Shopify section wrapper (e.g. "shopify-section-template--12345--main")
    const sectionWrapper = this.container.closest('.shopify-section');
    if (sectionWrapper && sectionWrapper.id.startsWith('shopify-section-')) {
      this.sectionId = sectionWrapper.id.replace('shopify-section-', '');
    } else {
      this.sectionId = null;
    }

    // Use a single bound handler to avoid stacking listeners
    this._handleClick = this.loadMore.bind(this);
    this.bindButton();
  }

  bindButton() {
    this.button = this.paginationWrapper.querySelector('[data-load-more]');
    this.loader = this.paginationWrapper.querySelector('[data-load-more-loader]');
    this.buttonText = this.paginationWrapper.querySelector('[data-load-more-text]');

    if (this.button) {
      this.button.addEventListener('click', this._handleClick);
    }
  }

  async loadMore() {
    const nextUrl = this.button.getAttribute('data-next-url');
    if (!nextUrl) return;

    // Validate URL is same-origin
    let url;
    try {
      url = new URL(nextUrl, window.location.origin);
      if (url.origin !== window.location.origin) {
        console.error('Invalid URL origin');
        return;
      }
    } catch (e) {
      console.error('Invalid URL format');
      return;
    }

    this.setLoadingState(true);

    try {
      let html;

      if (this.sectionId) {
        // Use Section Rendering API — fetches only the collection section
        url.searchParams.set('sections', this.sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const json = await response.json();
        html = json[this.sectionId];

        // Fallback if the section key didn't match
        if (!html) {
          const keys = Object.keys(json);
          html = keys.length > 0 ? json[keys[0]] : null;
        }
      }

      // Fallback to full page fetch if section rendering didn't work
      if (!html) {
        const response = await fetch(nextUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        html = await response.text();
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newProductsGrid = doc.querySelector('[data-products-grid]');
      if (!newProductsGrid) {
        console.error('Could not find products grid in response');
        this.setLoadingState(false);
        return;
      }

      // Extract and append new products
      const newGrids = newProductsGrid.querySelectorAll('.grid');
      if (newGrids.length === 0) {
        console.error('No grid elements found in response');
        this.setLoadingState(false);
        return;
      }

      // Get new pagination before moving nodes out of the parsed doc
      const newPagination = newProductsGrid.querySelector('[data-pagination]')
        || doc.querySelector('[data-pagination]');

      // Append each grid as its own sibling (not flattened into the last grid) so
      // sticky/grid-row scoping on blocks like .collection-media-block, which is
      // scoped to its own grid container, keeps working after load-more.
      // insertBefore(node, null) === appendChild, so this also covers a detached wrapper
      const anchor = this.paginationWrapper.parentNode === this.container ? this.paginationWrapper : null;
      newGrids.forEach((grid) => {
        this.container.insertBefore(grid, anchor);
      });

      this.reinitializeScripts();

      if (newPagination) {
        // Remove old listener before replacing
        if (this.button) {
          this.button.removeEventListener('click', this._handleClick);
        }
        this.paginationWrapper.replaceWith(newPagination);
        this.paginationWrapper = newPagination;
        this.bindButton();
      } else {
        this.paginationWrapper.remove();
        this.button = null;
        this.loader = null;
        this.buttonText = null;
      }

      this.setLoadingState(false);

    } catch (error) {
      console.error('Error loading more products:', error);
      this.setLoadingState(false);
      this.showError();
    }
  }

  setLoadingState(isLoading) {
    if (!this.button) return;

    if (isLoading) {
      this.button.disabled = true;
      this.button.style.opacity = '0';
      if (this.loader) {
        this.loader.removeAttribute('hidden');
      }
    } else {
      this.button.disabled = false;
      this.button.style.opacity = '1';
      if (this.loader) {
        this.loader.setAttribute('hidden', '');
      }
    }
  }

  showError() {
    if (this.buttonText) {
      this.buttonText.textContent = 'Error loading products';
      setTimeout(() => {
        this.buttonText.textContent = 'Load More';
      }, 3000);
    }
  }

  reinitializeScripts() {
    if (typeof window.initLinkedProducts === 'function') {
      window.initLinkedProducts();
    }

    document.dispatchEvent(new CustomEvent('load-more:complete'));
  }
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LoadMorePagination();
  });
} else {
  new LoadMorePagination();
}

// Re-initialize when collection filters are applied
document.addEventListener('collection-filters:updated', () => {
  new LoadMorePagination();
});

// Re-initialize when the theme editor replaces the section (e.g. a setting/block toggle)
document.addEventListener('shopify:section:load', () => {
  new LoadMorePagination();
});
